import React, { useState, useEffect } from 'react';
import { useSoftphone } from '../../hooks/useSoftphone';
import { Settings, Mic, Volume2, AlertTriangle, X, SlidersHorizontal } from 'lucide-react';

// Panel de selección de HARDWARE de audio del softphone: detecta los dispositivos
// conectados al PC (navigator.mediaDevices), permite elegir micrófono y altavoz, y
// muestra un medidor de nivel para que el agente verifique que su mic capta.
// Si no hay micrófono o salida de audio, muestra error (ver audioError del contexto).
export default function AudioSettings({ onClose }) {
  const {
    audioDevices, selectedMic, selectedSpeaker, setSelectedMic, setSelectedSpeaker,
    micLevel, startMicTest, stopMicTest, audioError, remoteAudioRef,
    noiseSuppression, echoCancellation, autoGainControl, micGain, speakerVolume,
    setNoiseSuppression, setEchoCancellation, setAutoGainControl, setMicGain, setSpeakerVolume,
  } = useSoftphone();
  const [testing, setTesting] = useState(false);

  useEffect(() => () => stopMicTest(), [stopMicTest]);

  const toggleTest = () => {
    if (testing) { stopMicTest(); setTesting(false); }
    else { startMicTest(); setTesting(true); }
  };

  const testSpeaker = () => {
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const ctx = new AudioCtx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = 440; gain.gain.value = 0.15;
      osc.connect(gain); gain.connect(ctx.destination);
      osc.start(); osc.stop(ctx.currentTime + 0.4);
      setTimeout(() => { try { ctx.close(); } catch {} }, 700);
    } catch {}
  };

  const canPickSpeaker = typeof remoteAudioRef?.current?.setSinkId === 'function';
  const label = (d, i, kind) => d.label || `${kind} ${i + 1}`;

  return (
    <div className="p-5 space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-gray-100 flex items-center gap-2 tracking-tight">
          <Settings size={16} /> Dispositivos de audio
        </span>
        {onClose && <button onClick={onClose} aria-label="Cerrar ajustes de audio" className="p-1.5 rounded-full text-gray-400 hover:text-white hover:bg-gray-800"><X size={16} /></button>}
      </div>

      {audioError && (
        <div className="flex items-start gap-2 bg-red-900/30 border border-red-700 text-red-300 rounded-2xl p-3 text-xs">
          <AlertTriangle size={16} className="flex-none mt-0.5" /><span>{audioError}</span>
        </div>
      )}

      {/* Micrófono */}
      <div>
        <label className="text-xs text-gray-400 flex items-center gap-1 mb-1.5" htmlFor="audio-mic-select"><Mic size={12} /> Micrófono</label>
        <select
          id="audio-mic-select"
          value={selectedMic}
          onChange={(e) => setSelectedMic(e.target.value)}
          className="w-full bg-gray-800/70 text-white text-sm rounded-full px-4 py-2.5 outline-none border border-gray-700/60 focus-visible:ring-2 focus-visible:ring-blue-500"
        >
          <option value="">Predeterminado del sistema</option>
          {audioDevices.mics.map((d, i) => (
            <option key={d.deviceId} value={d.deviceId}>{label(d, i, 'Micrófono')}</option>
          ))}
        </select>
        {audioDevices.mics.length === 0 && (
          <div className="text-[11px] text-red-400 mt-1.5 px-1">⚠ No se detectó ningún micrófono conectado.</div>
        )}
        <div className="mt-2.5 flex items-center gap-2">
          <button
            onClick={toggleTest}
            disabled={audioDevices.mics.length === 0}
            className={`text-xs px-3 py-1.5 rounded-full whitespace-nowrap disabled:opacity-40
              ${testing ? 'bg-red-600/30 text-red-300' : 'bg-blue-600/30 text-blue-300'}`}
          >
            {testing ? 'Detener' : 'Probar mic'}
          </button>
          <div className="flex-1 h-2 bg-gray-700 rounded-full overflow-hidden">
            {/* transform:scaleX() en vez de width: anima layout en cada lectura
                del micrófono (varias veces por segundo mientras se prueba);
                scaleX es solo compositor, sin recalcular layout ni pintar de
                nuevo (/impeccable audit, 24-sep-2026). */}
            <div
              className="h-full bg-green-500 origin-left"
              style={{ transform: `scaleX(${micLevel / 100})`, transition: 'transform 75ms linear' }}
            />
          </div>
        </div>
        {testing && (
          <div className="text-[11px] text-muted mt-1.5 px-1">Habla: la barra debe moverse. Si no se mueve, tu micrófono no capta.</div>
        )}
      </div>

      {/* Altavoz / Auriculares */}
      <div>
        <label className="text-xs text-gray-400 flex items-center gap-1 mb-1.5" htmlFor="audio-speaker-select"><Volume2 size={12} /> Altavoz / Auriculares</label>
        <select
          id="audio-speaker-select"
          value={selectedSpeaker}
          onChange={(e) => setSelectedSpeaker(e.target.value)}
          disabled={!canPickSpeaker}
          className="w-full bg-gray-800/70 text-white text-sm rounded-full px-4 py-2.5 outline-none border border-gray-700/60 focus-visible:ring-2 focus-visible:ring-blue-500 disabled:opacity-60"
        >
          <option value="">Predeterminado del sistema</option>
          {audioDevices.speakers.map((d, i) => (
            <option key={d.deviceId} value={d.deviceId}>{label(d, i, 'Altavoz')}</option>
          ))}
        </select>
        {audioDevices.speakers.length === 0 && (
          <div className="text-[11px] text-red-400 mt-1.5 px-1">⚠ No se detectó ninguna salida de audio.</div>
        )}
        <button onClick={testSpeaker} className="mt-2.5 text-xs px-3 py-1.5 rounded-full bg-blue-600/30 text-blue-300">Probar altavoz</button>
        {!canPickSpeaker && (
          <div className="text-[11px] text-muted mt-1.5 px-1">Tu navegador no permite elegir la salida; usa el predeterminado del sistema.</div>
        )}
      </div>

      <div className="border-t border-gray-700 pt-4 space-y-4">
        <span className="text-xs font-medium text-gray-300 flex items-center gap-1">
          <SlidersHorizontal size={12} /> Ajustes de audio
        </span>

        {/* Volumen de micrófono (ganancia) */}
        <div>
          <label className="text-xs text-gray-400 flex items-center justify-between mb-1" htmlFor="audio-mic-gain">
            <span className="flex items-center gap-1"><Mic size={12} /> Volumen del micrófono</span>
            <span className="text-muted">{micGain}%</span>
          </label>
          <input
            id="audio-mic-gain"
            type="range" min="0" max="200" step="5" value={micGain}
            onChange={(e) => setMicGain(Number(e.target.value))}
            className="w-full accent-blue-500"
          />
        </div>

        {/* Volumen de altavoz */}
        <div>
          <label className="text-xs text-gray-400 flex items-center justify-between mb-1" htmlFor="audio-speaker-volume">
            <span className="flex items-center gap-1"><Volume2 size={12} /> Volumen del altavoz</span>
            <span className="text-muted">{speakerVolume}%</span>
          </label>
          <input
            id="audio-speaker-volume"
            type="range" min="0" max="100" step="5" value={speakerVolume}
            onChange={(e) => setSpeakerVolume(Number(e.target.value))}
            className="w-full accent-blue-500"
          />
        </div>

        {/* Toggles de procesamiento nativo del navegador */}
        {[
          ['Cancelación de ruido', noiseSuppression, setNoiseSuppression],
          ['Cancelación de eco', echoCancellation, setEchoCancellation],
          ['Control automático de ganancia', autoGainControl, setAutoGainControl],
        ].map(([txt, val, set]) => (
          <label key={txt} className="flex items-center justify-between text-xs text-gray-300 cursor-pointer bg-gray-800/50 rounded-full px-4 py-2.5">
            <span>{txt}</span>
            <input type="checkbox" checked={val} onChange={(e) => set(e.target.checked)} className="accent-blue-500 w-4 h-4" />
          </label>
        ))}
        <div className="text-[11px] text-muted px-1">
          Ruido/eco/ganancia se aplican al instante. El volumen del micrófono afecta a la llamada en curso.
        </div>
      </div>
    </div>
  );
}
